/**
 * RateLimitModal Component
 * Displays when daily invoice limit is reached, with options to watch ads or upgrade
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
// import { invoiceApi } from '@/lib/api/invoice';
import type { RateLimitData } from '@/lib/api/types';
// import { logger } from '@/utils/logger';
import { LIMITS } from '@shared/constants';

interface RateLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  limitData: RateLimitData;
  sessionId: string | null;
  onBonusAdded?: () => void;
}

export function RateLimitModal({
  isOpen,
  onClose,
  limitData,
  // sessionId,
  // onBonusAdded,
}: RateLimitModalProps) {
  const { t } = useTranslation();
  // TODO: Implement ad watching functionality later
  // const [isWatchingAd, setIsWatchingAd] = useState(false);
  // const [adWatched, setAdWatched] = useState(false);
  const [adWatched] = useState(false);

  if (!isOpen) return null;

  const resetTime = new Date(limitData.resetTime);
  const timeUntilReset = resetTime.getTime() - Date.now();
  const hoursUntilReset = Math.floor(timeUntilReset / (1000 * 60 * 60));
  const minutesUntilReset = Math.floor((timeUntilReset % (1000 * 60 * 60)) / (1000 * 60));

  // TODO: Implement ad watching functionality later
  // const handleWatchAd = async () => {
  //   if (!sessionId) {
  //     alert('Session ID not found. Please refresh the page.');
  //     return;
  //   }
  //
  //   setIsWatchingAd(true);
  //
  //   // Simulate watching an ad (in production, integrate with actual ad network)
  //   setTimeout(async () => {
  //     try {
  //       // Add bonus invoices after watching ad
  //       const response = await invoiceApi.addBonusInvoices(sessionId, LIMITS.AD_BONUS_AMOUNT);
  //
  //       if (response.success) {
  //         setAdWatched(true);
  //         onBonusAdded?.();
  //
  //         // Close modal after showing success message
  //         setTimeout(() => {
  //           onClose();
  //           setAdWatched(false);
  //         }, 2000);
  //       } else {
  //         alert('Failed to add bonus invoices. Please try again.');
  //       }
  //     } catch (error) {
  //       logger.error('Error adding bonus:', error);
  //       alert('Failed to add bonus invoices. Please try again.');
  //     } finally {
  //       setIsWatchingAd(false);
  //     }
  //   }, 3000); // Simulate 3-second ad
  // };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
        {!adWatched ? (
          <>
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-amber-600 dark:text-amber-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {t('rateLimit.title')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {t('rateLimit.message')}
              </p>
            </div>

            {/* Time until reset */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-900 dark:text-blue-300">
                <span className="font-semibold">
                  {hoursUntilReset > 0 && minutesUntilReset > 0
                    ? t('rateLimit.wait', { hours: hoursUntilReset, minutes: minutesUntilReset })
                    : hoursUntilReset > 0
                    ? t('rateLimit.waitHoursOnly', { hours: hoursUntilReset })
                    : t('rateLimit.waitMinutesOnly', { minutes: minutesUntilReset })}
                </span>
              </p>
            </div>

            {/* Options */}
            <div className="space-y-3">
              {/* Watch Ad Option - TODO: Implement later */}
              {/* <button
                onClick={handleWatchAd}
                disabled={isWatchingAd}
                className="w-full btn-primary flex items-center justify-center gap-2 py-4"
              >
                {isWatchingAd ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {t('ad.watching')}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {t('ad.watchToGetBonus', { count: LIMITS.AD_BONUS_AMOUNT })}
                  </>
                )}
              </button> */}

              {/* Upgrade Option */}
              <button className="w-full btn-secondary dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 flex items-center justify-center gap-2 py-4">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                  />
                </svg>
                {t('rateLimit.upgrade')}
              </button>
            </div>

            {/* Close button */}
            <button
              onClick={onClose}
              className="w-full mt-4 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            >
              {t('rateLimit.close')}
            </button>
          </>
        ) : (
          // Success state
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t('ad.bonusAdded')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {t('ad.bonusMessage', { count: LIMITS.AD_BONUS_AMOUNT })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
